/**
 * Types
 *
 * A Theme is an object where each key is the name of a category and each value is a CategoryTheme
 *
 * CategoryTheme
 *
 * A CategoryTheme is a collection of default drawing properties for a category. A Feature can override the
 * default properties of its theme. The properties in a CategoryTheme are:
 *
 *    fillColor
 *    strokeColor
 *    strokeAlign
 *    strokeWidth
 *    textColor
 *    fontName
 *    fontSize
 *
 * During asset generation a Feature has its size reduced by having any properties that match the canonical
 * values for its category. At runtime, before the Feature is added to Mapbox, canonical properties are added back in
 *
 * @module DataTypes.Theme
 *
 */
import { keys, omit, path, assoc, mergeRight, map, filter, without } from 'ramda';

// a revoltingly colored category meant to highlight visually that a required category was missing from a theme
const themeForMissingCategory = {
    fillColor: '#0000FF',
    fontName: 'Open Sans',
    fontSize: 10,
    strokeAlign: 'center',
    strokeColor: '#00FF00',
    strokeWidth: 0.5,
    textColor: '#00FF00',
};

// "best-we-can-do" default values for any missing key of the category theme
const themeDefaults = {
    fillColor: '#C9C9C9',
    fontName: 'Open Sans',
    fontSize: 3,
    strokeAlign: 'center',
    strokeColor: '#000000',
    strokeWidth: 1,
    textColor: '#000000',
};

/*
 * Given a theme and an object with a "category" key, remove all the canonical properties for the category
 * @sig removeDefaultPropertiesFromFeature :: (Theme, Object) -> Object
 */
const removeDefaultPropertiesFromFeature = (theme, properties) => {
    const category = properties.category;

    if (!category) {
        throw new Error(`no category found in ${JSON.stringify(properties)}`);
    }
    if (!theme[category]) {
        throw new Error(`no theme found for category ${category}`);
    }

    // TODO: is it right to remove *all* the keys rather than only those that match?
    // (I *was* removing only those properties that *matched* values from the canonical theme
    // but now I'm removing *all* the keys whether they match or not, in order to force the idea that
    // you can't have "bespoke" properties for a feature in Illustrator
    const everyKeyThatCanBeThemed = keys(theme[category]);
    return omit(everyKeyThatCanBeThemed, properties);

    // return Functional.removeCanonicalProperties(theme[category], properties)
};

/*
 * Given a feature with a category, remove all the values in the feature's properties that match the feature's category
 * @sig removeDefaultPropertiesForCategoryFromFeature :: (Theme, Feature) -> Feature
 */
const removeDefaultPropertiesForCategoryFromFeature = (theme, feature) => {
    const category = path(['properties', 'category'], feature);
    const hasCategory = category && category.length;

    return hasCategory
        ? assoc('properties', removeDefaultPropertiesFromFeature(theme, feature.properties), feature)
        : feature;
};

/*
 * Return the categoryTheme for either the category or the "missing" category
 * @sig themeForCategory :: (Theme, String) -> CategoryTheme
 *    CategoryTheme = Object
 *    Theme = { categoryName: CategoryTheme }
 */
const themeForCategory = (theme, category) => {
    const categoryTheme = theme[category];

    if (!categoryTheme) {
        // eslint-disable-next-line no-console
        console.warn(`Unable to find category '${category}' in theme: using "missing" category instead`);
    }

    return categoryTheme || themeForMissingCategory;
};

/*
 * Default every missing value in theme with the corresponding value from the theme's "default" key or,
 * failing that, the hard-coded themeDefaults
 */
const enrichWithDefaults = theme => {
    const defaults = mergeRight(themeDefaults, theme.default || {});
    theme = map(o => mergeRight(defaults, o), theme);
    return theme;
};

const badgesInTheme = theme => {
    const categoriesWithBadges = filter(c => c.hasBadge, theme);
    return without('default', keys(categoriesWithBadges)); // default *says* it has a badge--but it doesn't
};

export { enrichWithDefaults, removeDefaultPropertiesForCategoryFromFeature, themeForCategory, badgesInTheme };
